/**
 * Dark-theme dashboard cards: Farm-Outs report + Weekly Payroll Preview.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { ArrowRightLeft, Wallet, ChevronRight, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt$ = (n) => `$${Number(n || 0).toLocaleString()}`;

export const FarmoutsCard = ({ onJumpTab }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/ops/farmouts`).then(r => r.json()).then(setData).catch(() => {});
  }, []);
  const t = data?.totals || {};
  const rows = (data?.farmouts || []).slice(0, 4);
  return (
    <Card className="bg-gray-950 border-gray-800 overflow-hidden" data-testid="card-farmouts">
      <div className="p-4 border-b border-gray-800 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ArrowRightLeft className="h-4 w-4 text-amber-400" />
          <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Farm-Outs ({t.count || 0})</p>
        </div>
        <button onClick={() => onJumpTab && onJumpTab('quotebookings')} className="text-[10px] text-red-500 font-bold flex items-center gap-1 hover:text-red-400" data-testid="farmouts-view-all">
          Bookings <ChevronRight className="h-3 w-3" />
        </button>
      </div>
      <div className="grid grid-cols-3 divide-x divide-gray-800 text-center border-b border-gray-800">
        {[['Revenue', t.revenue, 'text-white'], ['Payable', t.payable, 'text-amber-400'], ['Margin', t.margin, 'text-emerald-400']].map(([l, v, c]) => (
          <div key={l} className="py-3">
            <p className={`text-lg font-black ${c}`} data-testid={`farmouts-${String(l).toLowerCase()}`}>{fmt$(v)}</p>
            <p className="text-[9px] uppercase tracking-widest text-gray-500 font-bold">{l}</p>
          </div>
        ))}
      </div>
      {rows.length === 0 ? (
        <p className="text-[11px] text-gray-600 text-center py-4">No farmed-out trips. Flag one via Trip Details+ on any booking.</p>
      ) : (
        <div className="divide-y divide-gray-800">
          {rows.map(f => (
            <div key={f.booking_id} className="px-4 py-2.5 flex items-center gap-3" data-testid={`farmout-row-${f.booking_id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-white truncate">{f.client_name} · {f.event_date}</p>
                <p className="text-[10px] text-gray-500 truncate">→ {f.farmout_partner || 'Partner TBD'} {f.farmout_confirmation ? `· ${f.farmout_confirmation}` : ''}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className={`text-xs font-black ${f.margin >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{fmt$(f.margin)}</p>
                <p className="text-[9px] text-gray-600">{fmt$(f.total)} − {fmt$(f.farmout_cost)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export const PayrollPreviewCard = ({ onJumpTab }) => {
  const [data, setData] = useState(null);
  const [approving, setApproving] = useState(false);

  const load = useCallback(() => {
    fetch(`${API}/api/drivers/payroll/weekly`).then(r => r.json()).then(setData).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const approveAll = async () => {
    if (!window.confirm('Approve ALL closed shifts for this week? They become eligible for the payroll run.')) return;
    setApproving(true);
    try {
      const r = await fetch(`${API}/api/drivers/payroll/approve-week`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ week_start: data?.week_start }),
      });
      const d = await r.json();
      toast.success(`${d.approved} shift${d.approved === 1 ? '' : 's'} approved for ${d.week_label}`);
      load();
    } catch { toast.error('Approve failed'); }
    setApproving(false);
  };

  const t = data?.totals || {};
  const rows = data?.drivers || [];
  return (
    <Card className="bg-gray-950 border-gray-800 overflow-hidden" data-testid="card-payroll-preview">
      <div className="p-4 border-b border-gray-800 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Wallet className="h-4 w-4 text-emerald-400" />
          <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">
            Payroll Preview {data ? `· ${data.week_start} → ${data.week_end}` : ''}
          </p>
        </div>
        <button onClick={() => onJumpTab && onJumpTab('teampay')} className="text-[10px] text-red-500 font-bold flex items-center gap-1 hover:text-red-400" data-testid="payroll-open-tab">
          Run Payroll <ChevronRight className="h-3 w-3" />
        </button>
      </div>
      <div className="grid grid-cols-3 divide-x divide-gray-800 text-center border-b border-gray-800">
        {[['Hours', t.total_hours || 0, 'text-white', false], ['Gratuity', t.total_gratuity, 'text-amber-400', true], ['Payable', t.total_payable, 'text-emerald-400', true]].map(([l, v, c, money]) => (
          <div key={l} className="py-3">
            <p className={`text-lg font-black ${c}`} data-testid={`payroll-${String(l).toLowerCase()}`}>{money ? fmt$(v) : v}</p>
            <p className="text-[9px] uppercase tracking-widest text-gray-500 font-bold">{l}</p>
          </div>
        ))}
      </div>
      {rows.length === 0 ? (
        <p className="text-[11px] text-gray-600 text-center py-4">No shifts this week — trip clock-ins land here automatically.</p>
      ) : (
        <div className="divide-y divide-gray-800">
          {rows.map(d => (
            <div key={d.driver_id} className="px-4 py-2.5 flex items-center gap-3" data-testid={`payroll-driver-${d.driver_id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-white truncate">{d.name}</p>
                <p className="text-[10px] text-gray-500">{d.total_hours}h × ${d.hourly_rate}/hr {d.gratuity_total > 0 ? `+ ${fmt$(d.gratuity_total)} tips` : ''}</p>
              </div>
              <p className="text-xs font-black text-emerald-400 flex-shrink-0">{fmt$(d.total_payable)}</p>
              {d.all_approved
                ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" title="All approved" />
                : <span className="text-[8px] uppercase font-bold text-amber-400 border border-amber-800 px-1.5 py-0.5 rounded flex-shrink-0">Review</span>}
            </div>
          ))}
        </div>
      )}
      {rows.length > 0 && !t.all_approved && (
        <div className="p-3 border-t border-gray-800">
          <button onClick={approveAll} disabled={approving} className="w-full py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-[11px] font-bold uppercase tracking-wider rounded transition-colors disabled:opacity-50" data-testid="payroll-approve-all">
            {approving ? 'Approving…' : '✓ Approve All Shifts — 1-Click Payday Prep'}
          </button>
        </div>
      )}
    </Card>
  );
};
